import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface ArenaInteraction {
  id: string;
  arena_id: string;
  from_user_id: string;
  to_user_id: string;
  interaction_type: "pop" | "like" | "view_profile";
  created_at: string;
}

interface ArenaSocialProof {
  watcherCount: number;
  recentPops: number;
  isHeatingUp: boolean;
}

export const useArenaInteractions = (arenaId: string | null) => {
  const { user } = useAuthContext();
  const [interactions, setInteractions] = useState<ArenaInteraction[]>([]);
  const [socialProof, setSocialProof] = useState<ArenaSocialProof>({
    watcherCount: 0,
    recentPops: 0,
    isHeatingUp: false,
  });

  // Fetch recent interactions
  const fetchInteractions = useCallback(async () => {
    if (!arenaId) return;

    const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000).toISOString();

    const { data } = await supabase
      .from("arena_interactions")
      .select("*")
      .eq("arena_id", arenaId)
      .gte("created_at", twoMinutesAgo)
      .order("created_at", { ascending: false });

    if (data) {
      setInteractions(data as ArenaInteraction[]);
      const recentPops = data.filter(i => i.interaction_type === "pop").length;
      setSocialProof({
        watcherCount: data.filter(i => i.interaction_type === "view_profile").length,
        recentPops,
        isHeatingUp: recentPops >= 3,
      });
    }
  }, [arenaId]);

  // Subscribe to realtime interactions
  useEffect(() => {
    if (!arenaId) return;

    fetchInteractions();

    const channel = supabase
      .channel(`arena_interactions_${arenaId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "arena_interactions",
          filter: `arena_id=eq.${arenaId}`,
        },
        (payload) => {
          setInteractions(prev => [payload.new as ArenaInteraction, ...prev]);
          if ((payload.new as ArenaInteraction).interaction_type === "pop") {
            setSocialProof(prev => ({
              ...prev,
              recentPops: prev.recentPops + 1,
              isHeatingUp: prev.recentPops + 1 >= 3,
            }));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [arenaId, fetchInteractions]);

  // Pop a user
  const popUser = useCallback(async (toUserId: string, isExhibition: boolean = false) => {
    if (!user || !arenaId) return { error: new Error("Not authenticated") };

    const { error } = await supabase
      .from("arena_interactions")
      .insert({
        arena_id: arenaId,
        from_user_id: user.id,
        to_user_id: toUserId,
        interaction_type: "pop",
      });

    // Also create a pop in the pops table for matching
    if (!error) {
      await supabase.from("pops").insert({
        popper_id: user.id,
        popped_id: toUserId,
        tab_context: isExhibition ? "love_exhibition" : "live_arena",
      });

      // Check if it's a mutual pop (match)
      const { data: mutualPop } = await supabase
        .from("pops")
        .select("id")
        .eq("popper_id", toUserId)
        .eq("popped_id", user.id)
        .maybeSingle();

      if (mutualPop) {
        // Create match in unified matches table
        const user1 = user.id < toUserId ? user.id : toUserId;
        const user2 = user.id < toUserId ? toUserId : user.id;
        await supabase.from("matches").insert({
          user1_id: user1,
          user2_id: user2,
          met_context: isExhibition ? "love_exhibition" : "live_arena",
          match_source: isExhibition ? "exhibition" : "pop_balloon",
          arena_id: arenaId,
          source_context: { arena_id: arenaId, is_exhibition: isExhibition },
        });
      }
    }

    return { error };
  }, [user, arenaId]);

  // Like a user
  const likeUser = useCallback(async (toUserId: string, isExhibition: boolean = false) => {
    if (!user || !arenaId) return { error: new Error("Not authenticated") };

    const { error } = await supabase
      .from("arena_interactions")
      .insert({
        arena_id: arenaId,
        from_user_id: user.id,
        to_user_id: toUserId,
        interaction_type: "like",
      });

    // Also create a like in the likes table
    if (!error) {
      await supabase.from("likes").insert({
        liker_id: user.id,
        liked_id: toUserId,
        tab_context: isExhibition ? "love_exhibition" : "live_arena",
      });
    }

    return { error };
  }, [user, arenaId]);

  // Track profile view
  const viewProfile = useCallback(async (toUserId: string) => {
    if (!user || !arenaId) return;

    await supabase.from("arena_interactions").insert({
      arena_id: arenaId,
      from_user_id: user.id,
      to_user_id: toUserId,
      interaction_type: "view_profile",
    });
  }, [user, arenaId]);

  // Check if user received pop back (match)
  const checkForMatch = useCallback(async (toUserId: string) => {
    if (!user) return false;

    const { data } = await supabase
      .from("pops")
      .select("id")
      .eq("popper_id", toUserId)
      .eq("popped_id", user.id)
      .maybeSingle();

    return !!data;
  }, [user]);

  return {
    interactions,
    socialProof,
    popUser,
    likeUser,
    viewProfile,
    checkForMatch,
  };
};
